                                                import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentCategoryDTO } from '../DocumentCategory';
import { AssististService } from '../assistist.service';
import { MediaService, Media } from 'src/app/content/media.service';
import { SessionService } from 'src/app/session.service';
import { TitleService } from 'src/app/title.service';
import { UiService } from 'src/app/ui/ui.service';
import { NotificationService } from 'src/app/notifications/notification.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AreYouSureDialogComponent } from 'src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component';
import { Subject, of, merge, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map, takeUntil, catchError, first } from 'rxjs/operators';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { CanComponentDeactivate } from 'src/app/shared/guards/can-deactivate.guard';
import { getAssististDocumentLabels } from '../document-labels';

@Component({
  selector: 'tripist-categories-edit',
  templateUrl: './categories-edit.component.html',
  styleUrls: ['./categories-edit.component.scss']
})
export class assististCategoriesEditComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  newCategory!: boolean;
  category?: DocumentCategoryDTO;
  categoryForm!: FormGroup;
  filteredMedias: Media[] = [];
  selectedMedia?: Media;
  
  // File upload properties
  uploadFile: File | undefined;
  uploadImageWidth: number | undefined;
  uploadImageHeight: number | undefined;
  isUploading: boolean = false;
  

  
  private onDestroy = new Subject<void>();
  private integrationID!: number;
  featureType: string = '';

  get categoryTypeText(): string {
    return `${getAssististDocumentLabels(this.featureType).singular} Category`;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private TitleService: TitleService,
    private session: SessionService,
    private uiService: UiService,
    private NotificationService: NotificationService,
    private assististService: AssististService,
    private mediaService: MediaService,
    private dialog: MatDialog,
  ) {}

    ngOnInit(): void {
    // Subscribe to route parameter and data changes to handle navigation between tripist/explorist
    merge(
      this.activatedRoute.params,
      this.activatedRoute.data
    ).pipe(
      takeUntil(this.onDestroy)
    ).subscribe(() => {
      this.updateFromRouteParams();
    });
    
    // Also handle initial load
    this.updateFromRouteParams();
    
    // Add beforeunload listener to prevent accidental page close/refresh with unsaved changes
    this.addBeforeUnloadListener();
  }

  private updateFromRouteParams(): void {
    // Get the feature type from the route to determine provider type
    const featureType = this.activatedRoute.snapshot.params['featureType']; // 'tripist' | 'explorist' | 'aura'
    this.featureType = featureType;
    const providerType = featureType === 'tripist'
      ? 'tripist_assistant'
      : featureType === 'aura'
        ? 'aura_assistant'
        : 'explorist_assistant';

    // grab the resolved integrations & find the correct assistant one
    const integrations = this.activatedRoute.snapshot.data.integrations || [];
    const integration = (integrations as any[]).find(i => i.provider.type === providerType);
    this.integrationID = integration.id;

    this.category = this.activatedRoute.snapshot.data.category;
    this.newCategory = !this.category;

    if (!this.newCategory) {
      this.TitleService.setTitle(
        this.TitleService.getTitle() + ` | ${this.category?.name}`
      );
    }

    this.initForm();
    this.hookMediaSearch();
  }

  ngOnDestroy(): void {
    this.removeBeforeUnloadListener();
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  private initForm() {
    this.categoryForm = new FormGroup({
      name: new FormControl(this.category?.name, Validators.required),
      mediaSearch: new FormControl(''),
      imageName: new FormControl(''),  // Remove required validator - will be added conditionally
    });

    if (this.category?.media) {
      this.selectedMedia = this.category.media;
    }

    // Mark form as pristine after loading existing data
    if (!this.newCategory) {
      this.categoryForm.markAsPristine();
    }
  }

  private hookMediaSearch() {
    // Get the feature type for the media context
    const featureType = this.activatedRoute.snapshot.params['featureType']; // 'tripist' or 'explorist'
    const mediaContext = `${featureType}_category_image`;

    this.categoryForm
      .get('mediaSearch')!
      .valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) =>
          this.mediaService
            .getMedias(this.currentAccountID, {
              page: 0,
              perPage: 10,
              context: mediaContext,
              keyword: term,
              forCount: false,
            } as any)
            .pipe(catchError(() => of({ records: [] })))
        ),
        map((resp: any) => resp.records),
        takeUntil(this.onDestroy)
      )
      .subscribe((medias: Media[]) => {
        this.filteredMedias = medias;
      });
  }

  onMediaSelected(media: Media) {
    this.selectedMedia = media;
    this.categoryForm.get('mediaSearch')!.setValue('');
    // Mark form as dirty when image is selected
    this.categoryForm.markAsDirty();
    // Also mark it as touched to ensure change detection
    this.categoryForm.markAsTouched();
  }

  clearMedia() {
    this.selectedMedia = undefined;
    this.categoryForm.get('mediaSearch')!.setValue('');
    // Mark form as dirty when image is cleared
    this.categoryForm.markAsDirty();
  }

  // File upload methods
  dropped(files: NgxFileDropEntry[]): void {
    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          this.uploadFile = file;
          this.updateImageNameValidation();
          const reader = new FileReader();

          reader.readAsDataURL(file);

          reader.onload = (e) => {
            const image = new Image();
            image.src = e.target!.result as string;

            image.onload = () => {
              this.uploadImageWidth = image.width;
              this.uploadImageHeight = image.height;
            };
          };
        });
      }
    }
  }

  removeUploadFile(): void {
    this.uploadFile = undefined;
    this.uploadImageWidth = undefined;
    this.uploadImageHeight = undefined;
    this.updateImageNameValidation();
  }

  private updateImageNameValidation(): void {
    const imageNameControl = this.categoryForm.get('imageName');
    if (this.uploadFile) {
      // Make imageName required when uploading a file
      imageNameControl?.setValidators([Validators.required]);
    } else {
      // Remove required validator when not uploading
      imageNameControl?.clearValidators();
    }
    imageNameControl?.updateValueAndValidity();
  }

  uploadImage(): void {
    if (!this.uploadFile) return;

    this.isUploading = true;
    const featureType = this.activatedRoute.snapshot.params['featureType']; // 'tripist' or 'explorist'
    const mediaContext = `${featureType}_category_image`;
    const mediaDescription = this.categoryForm.get('imageName')?.value || this.uploadFile.name;

    this.uiService
      .waitForEvent(
        this.mediaService.postMedia(
          this.currentAccountID,
          this.uploadFile,
          mediaContext,
          mediaDescription
        )
      )
      .pipe(takeUntil(this.onDestroy))
      .subscribe(
        (response: any) => {
          this.NotificationService.openSuccessSnackBar('Image uploaded successfully');
          // Set the uploaded media as selected
          this.selectedMedia = response;
          // Clear the upload file
          this.removeUploadFile();
          this.isUploading = false;
          // Mark form as dirty when image is uploaded
          this.categoryForm.markAsDirty();
        },
        (error) => {
          this.NotificationService.openErrorSnackBar('Error uploading image');
          this.isUploading = false;
        }
      );
  }

  get uploadDisabled(): boolean {
    return !this.uploadFile || this.isUploading || !this.categoryForm.get('imageName')?.value;
  }

  get saveDisabled(): boolean {
    // disable save if form invalid OR no meaningful changes made
    const formInvalid = this.categoryForm.invalid;
    const hasChanges = this.hasMeaningfulChanges();
    return formInvalid || !hasChanges;
  }

  private hasMeaningfulChanges(): boolean {
    // Check if form is dirty, but exclude changes to mediaSearch field since it's just for searching
    if (!this.categoryForm.dirty) {
      return false;
    }
    
    // If only mediaSearch field is dirty, don't consider it a meaningful change
    const controls = this.categoryForm.controls;
    let hasOtherChanges = false;
    for (const [controlName, control] of Object.entries(controls)) {
      if (controlName !== 'mediaSearch' && control.dirty) {
        hasOtherChanges = true;
        break;
      }
    }
    
    // Also check if selectedMedia has changed (this would be a meaningful change)
    const mediaChanged = (this.selectedMedia && this.category?.media?.id !== this.selectedMedia.id) ||
                        (!this.selectedMedia && !!this.category?.media);
    
    return hasOtherChanges || mediaChanged;
  }

  private get currentAccountID(): number {
    return this.session.currentAccountValue!.accountID;
  }

  private buildDTO(): { name: string; media_id?: number | null; id?: number } {
    // pull name as a string
    const name = this.categoryForm.get('name')!.value as string;

    // assemble the payload with a required `name`
    const dto: { name: string; media_id?: number | null; id?: number } = {
      name,
      media_id: this.selectedMedia ? this.selectedMedia.id : null,
    };

    if (this.category) {
      dto.id = this.category.id;
    }
    return dto;
  }

  saveNewCategory() {
    this.uiService
      .waitForEvent(
        this.assististService.createDocumentCategory(
          this.currentAccountID,
          this.integrationID,
          this.buildDTO()
        )
      )
      .pipe(takeUntil(this.onDestroy))
      .subscribe(
        (res: any) => {
          this.NotificationService.openSuccessSnackBar(
            'Category created successfully'
          );
          // Reset form state after successful save
          this.resetFormState();
          // Small delay to ensure form state is reset before navigation
          setTimeout(() => {
            this.router.navigate(['list'], {
              relativeTo: this.activatedRoute.parent,
            });
          }, 0);
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            'Error creating category'
          );
        }
      );
  }

  saveCategoryUpdates() {
    this.uiService
      .waitForEvent(
        this.assististService.updateDocumentCategory(
          this.currentAccountID,
          this.integrationID,
          this.buildDTO() as DocumentCategoryDTO
        )
      )
      .pipe(takeUntil(this.onDestroy))
      .subscribe(
        () => {
          this.NotificationService.openSuccessSnackBar(
            'Category updated successfully'
          );
          // Reset form state after successful save
          this.resetFormState();
          // Small delay to ensure form state is reset before navigation
          setTimeout(() => {
            this.router.navigate(['list'], {
              relativeTo: this.activatedRoute.parent,
            });
          }, 0);
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            'Error updating category'
          );
        }
      );
  }

  deleteCategory() {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this category ( ${this.category!.name} )?`,
            warning: 'Warning: this is not reversible!',
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.uiService
            .waitForEvent(
              this.assististService.deleteDocumentCategory(
                this.currentAccountID,
                this.integrationID,
                this.category!.id
              )
            )
            .pipe(takeUntil(this.onDestroy))
            .subscribe(
              () => {
                this.NotificationService.openSuccessSnackBar(
                  'Category deleted successfully'
                );
                // Reset form state after successful deletion
                this.resetFormState();
                // Small delay to ensure form state is reset before navigation
                setTimeout(() => {
                  this.router.navigate(['list'], {
                    relativeTo: this.activatedRoute.parent,
                  });
                }, 0);
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  'Error deleting category'
                );
              }
            );
        }
      });
  }

  cancel() {
    // Reset form state when canceling
    this.resetFormState();
    this.router.navigate(['list'], {
      relativeTo: this.activatedRoute.parent,
    });
  }

  // CanComponentDeactivate interface implementation
  canDeactivate(): boolean | Observable<boolean> {
    return !this.hasUnsavedChanges();
  }

  hasUnsavedChanges(): boolean {
    return this.hasMeaningfulChanges();
  }

  // Method to reset form state after successful operations
  private resetFormState(): void {
    this.categoryForm.markAsPristine();
    this.categoryForm.markAsUntouched();
  }

  // Beforeunload protection
  private addBeforeUnloadListener(): void {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private removeBeforeUnloadListener(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private beforeUnloadHandler = (event: BeforeUnloadEvent): string | undefined => {
    if (this.hasUnsavedChanges()) {
      const message = 'You have unsaved changes. Are you sure you want to leave?';
      event.preventDefault();
      event.returnValue = message;
      return message;
    }
    return undefined;
  };
}
